import React from 'react';
import {Card, MiniAreaChart, Stat} from './primitives';
import {useSmartTestingCards} from '../hooks/useSmartTestingMetrics';
import type {SmartTestCardMetric} from '../services/smartTestingReportingService';

interface Props {
    /** MLD-side organization id; pulls per-card completion metrics scoped to this org's OHD-imported users. */
    mldOrganizationId?: number;
    /** OHD campaign id whose curation rows drive the card list. Required for the query to fire. */
    campaignId?: number | null;
}

/**
 * Per-test-panel completion cards on the OHD employer dashboard.
 * One card per panel ops has flipped show_as_card=true on (curated
 * via the OHD Panel Curation modal). Display name, gender filter,
 * sort order, and color are all sourced from the API — adding or
 * removing a card is a DB change, no FE deploy required.
 *
 * Engagement chart + 4-stat grid keeps the dashboard layout
 * consistent with the BioMatch program tiles.  Panels with placeholder=true
 * render with an "Awaiting Results" badge (currently Cotinine, until
 * the LabCorp send-out path is wired in — see CLAUDE.md "Cotinine /
 * LabCorp Send-Out").
 */
const SmartTestCardsSection: React.FC<Props> = ({mldOrganizationId, campaignId}) => {
    const query = useSmartTestingCards(mldOrganizationId, campaignId);
    if (!mldOrganizationId || !campaignId || query.isLoading || !query.data) return null;

    const cards = query.data.cards;
    if (cards.length === 0) return null;

    return (
        <div className="grid grid-cols-2 gap-5">
            {cards.map((card) => (
                <SmartTestPanelCard key={card.key} card={card} color={card.color} />
            ))}
        </div>
    );
};

const SmartTestPanelCard: React.FC<{card: SmartTestCardMetric; color: string}> = ({card, color}) => {
    const eligible = card.eligible_participants;
    const completed = card.completed_participants;
    const pending = Math.max(0, eligible - completed);
    const pct = eligible > 0 ? Math.round((completed / eligible) * 100) : 0;

    const genderLabel =
        card.gender_filter === 'm' ? 'Males Only' : card.gender_filter === 'f' ? 'Females Only' : null;

    // Until the BE returns a per-card daily completion series, the
    // chart shows a flat zero baseline.  Easy to swap to real data
    // later by adding `daily_series` to the BE response.
    const chartData = [0, 0, 0, 0, 0].map((v, i) => ({day: `Day ${i + 1}`, value: v}));

    return (
        <div>
            <div className="mb-2 flex items-center gap-2">
                <h3 className="text-[24px] text-mld-text-primary">{card.display_name}</h3>
                {card.placeholder ? (
                    <span className="rounded-md bg-mld-muted-text/40 px-3 py-1 text-[13px] font-medium text-white">
                        Awaiting Results
                    </span>
                ) : null}
            </div>
            <Card>
                <div className="p-5">
                    <div className="mb-4 p-4">
                        <div className="mb-2 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <span className="text-[14px] font-semibold text-mld-text-primary">Engagement</span>
                                {genderLabel && (
                                    <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[8px] font-semibold text-blue-700">
                                        {genderLabel}
                                    </span>
                                )}
                            </div>
                            <span className="text-[12px] text-mld-muted-text">
                                {card.placeholder
                                    ? 'Awaiting results'
                                    : completed === 0
                                      ? 'No results yet'
                                      : `${pct}% complete`}
                            </span>
                        </div>
                        <MiniAreaChart data={chartData} color={color} height={160} />
                    </div>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                        <Stat value={String(completed)} label="Participants Tested" />
                        <Stat value={String(eligible)} label="User Accounts" />
                        <Stat value={`${pct}%`} label="% Complete" />
                        <Stat value={String(pending)} label="Pending Results" />
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default SmartTestCardsSection;
